import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Navigation } from "lucide-react";
import { tiltToPitchRatio, azimuthToOrientation } from "@/utils/roofEdgeCalculations";

interface RoofFacetCardProps {
  facetNumber: number;
  areaSqFt: number;
  tiltDegrees: number | null;
  azimuthDegrees: number | null;
}

const directionColors: Record<string, string> = {
  North: "bg-blue-500",
  East: "bg-yellow-500",
  South: "bg-orange-500",
  West: "bg-purple-500",
};

export function RoofFacetCard({ facetNumber, areaSqFt, tiltDegrees, azimuthDegrees }: RoofFacetCardProps) {
  const pitchInfo = tiltDegrees !== null ? tiltToPitchRatio(tiltDegrees) : null;
  const orientation = azimuthDegrees !== null ? azimuthToOrientation(azimuthDegrees) : null;
  const colorClass = orientation ? directionColors[orientation] : "bg-gray-500";
  const squares = (areaSqFt / 100).toFixed(1);

  return (
    <Card className="border-2 hover:border-primary/50 transition-colors">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="font-semibold text-lg">Facet {facetNumber}</h4>
            <p className="text-sm text-muted-foreground">{areaSqFt.toLocaleString()} ft² ({squares} sq)</p>
          </div>
          <div className={`w-12 h-12 rounded-full ${colorClass} flex items-center justify-center`}>
            {azimuthDegrees !== null && (
              <Navigation 
                className="w-6 h-6 text-white" 
                style={{ transform: `rotate(${azimuthDegrees}deg)` }}
              />
            )}
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Pitch</span>
            {pitchInfo ? (
              <Badge variant="secondary" className="font-bold">
                {pitchInfo.pitch} ({pitchInfo.angle}°)
              </Badge>
            ) : (
              <Badge variant="secondary" className="font-medium text-muted-foreground">
                -/12
              </Badge>
            )}
          </div>
          
          {orientation && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Orientation</span>
              <Badge variant="outline" className={`${colorClass} text-white border-none`}>
                {orientation}
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
